import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Chip,
  Breadcrumbs,
  Link,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  useTheme,
  alpha,
  InputAdornment,
} from '@mui/material';
import {
  Folder,
  InsertDriveFile,
  CloudUpload,
  Download,
  Delete,
  CreateNewFolder,
  Lock,
  MoreVert,
  Refresh,
  Home,
  NavigateNext,
  Visibility,
  VisibilityOff,
  Search,
  GridView,
  ViewList,
  Clear,
  Description,
} from '@mui/icons-material';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { cacheManager } from '../../utils/cacheManager';
import { backendCacheService, invalidateAfterOperation } from '../../utils/backendCacheService';
import FileOperationsSpeedDial from './FileOperationsSpeedDial';

const EnhancedFileManager = () => {
  const theme = useTheme();
  const { user } = useAuth();
  
  // State management
  const [currentPath, setCurrentPath] = useState('/');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  
  // Dialog states
   const [createFolderDialog, setCreateFolderDialog] = useState(false);
   const [passwordDialog, setPasswordDialog] = useState(false);
   const [uploadDialog, setUploadDialog] = useState(false);
   const [fileViewerDialog, setFileViewerDialog] = useState(false);
  
  // Form data
   const [newFolderData, setNewFolderData] = useState({
     name: '',
     password: '',
     description: ''
   });
   const [passwordData, setPasswordData] = useState({
     folderPath: '',
     password: ''
   });
   const [uploadFiles, setUploadFiles] = useState([]);
   const [viewFileData, setViewFileData] = useState({
     filePath: '',
     fileName: '',
     fileType: '',
     fileContent: null,
     isTextFile: false
   });
  
  // UI states
  const [contextMenu, setContextMenu] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', type: 'info' });
  const [accessTokens, setAccessTokens] = useState({}); // Store folder access tokens
  const [downloadProgress, setDownloadProgress] = useState({}); // Track download progress for files
  const [deleteProgress, setDeleteProgress] = useState({}); // Track delete progress for files and folders
  const [uploadProgress, setUploadProgress] = useState({}); // Track upload progress for files
  const [createFolderProgress, setCreateFolderProgress] = useState(false); // Track folder creation progress
  
  // Password visibility states
  const [showNewFolderPassword, setShowNewFolderPassword] = useState(false);
  const [showVerifyPassword, setShowVerifyPassword] = useState(false);

  
  // Search functionality
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredFiles, setFilteredFiles] = useState([]);
  
  // Request debouncing to prevent rapid-fire API calls
  const [lastRequestTime, setLastRequestTime] = useState(0);
  const [requestInProgress, setRequestInProgress] = useState(false);
  const REQUEST_DEBOUNCE_MS = 500; // Wait 500ms between requests
  
  // Fetch files from current path with rate limiting
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchFiles = useCallback(async (path = currentPath, accessToken = null, forceRefresh = false) => {
    // Prevent duplicate requests
    if (requestInProgress && !forceRefresh) {
      console.log('🚫 Skipping duplicate request for path:', path);
      return;
    }
    
    // Debounce requests to prevent 429 errors
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTime;
    if (timeSinceLastRequest < REQUEST_DEBOUNCE_MS && !forceRefresh) {
      console.log('⏳ Debouncing request, waiting...', REQUEST_DEBOUNCE_MS - timeSinceLastRequest, 'ms');
      await new Promise(resolve => setTimeout(resolve, REQUEST_DEBOUNCE_MS - timeSinceLastRequest));
    }
    
    setLoading(true);
    setRequestInProgress(true);
    setLastRequestTime(Date.now());
    
    // Check cache first (unless force refresh)
    const cacheKey = cacheManager.generateKey(path, accessToken);
    if (!forceRefresh) {
      const cachedData = cacheManager.get(cacheKey);
      if (cachedData) {
        setFiles(cachedData);
        setLoading(false);
        setRequestInProgress(false);
        return;
      }
    }
    
    try {
      const params = { folderPath: path };
      if (accessToken) {
        params.accessToken = accessToken;
      }
      
      console.log('📡 Making API request to:', path);
      const response = await api.get('/api/folders/contents', {
        params
      });
      
      if (response.data.success) {
        let filesData = response.data.files;

        // Filter out the current folder from the results (prevent self-listing)
        const currentFolderName = path.split('/').pop();
        filesData = filesData.filter(file => {
          // Filter out files with the same path as current folder
          if (file.path === path) return false;

          // Also filter out files with the same name as current folder (additional safety)
          if (file.name === currentFolderName && file.isDirectory) {
            console.warn('Filtering out folder with same name as current folder:', file.name);
            return false;
          }

          return true;
        });

        // Remove duplicates based on file path and name combination
        const seenFiles = new Map();
        filesData = filesData.filter(file => {
          const key = `${file.path || file.name}_${file.isDirectory ? 'dir' : 'file'}`;
          if (seenFiles.has(key)) {
            console.warn('Duplicate file filtered out:', key, file);
            return false;
          }
          seenFiles.set(key, file);
          return true;
        });

        // No client-side filtering - backend handles access control
        // Users from same location should see all folders (protected or not)
        // Access control is enforced when opening folders, not when listing

        setFiles(filesData);
        
        // Cache the results
        cacheManager.set(cacheKey, filesData);
        console.log('✅ Files loaded successfully for path:', path);
      }
    } catch (error) {
      if (error.response?.status === 429) {
        // Rate limit hit - show specific error and wait longer
        console.error('🚫 Rate limit exceeded, waiting before retry...');
        showNotification('Too many requests - please wait a moment before trying again', 'warning');
        await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
      } else if (error.response?.status === 401) {
        if (error.response?.data?.protected) {
          // Folder is password protected
          console.log('🔒 Folder is password protected:', path);
          setPasswordData({ folderPath: path, password: '' });
          setPasswordDialog(true);
        } else {
          // General authentication error
          console.error('🔐 Authentication failed for folder:', path, error.response?.data);
          showNotification('Access denied: You may not have permission to view this folder', 'error');
        }
      } else {
        console.error('❌ Failed to load files:', error);
        showNotification('Failed to load files: ' + (error.response?.data?.error || error.message), 'error');
      }
    } finally {
      setLoading(false);
      setRequestInProgress(false);
    }
  }, [currentPath, lastRequestTime, requestInProgress]);
  
  // Debounced navigation to prevent rapid API calls
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const NAVIGATION_DEBOUNCE_MS = 300;
  
  // Handle debounced navigation
  useEffect(() => {
    if (!pendingNavigation) return;
    
    const timeoutId = setTimeout(() => {
      console.log('🚀 Executing debounced navigation to:', pendingNavigation.path);
      fetchFiles(pendingNavigation.path, pendingNavigation.accessToken);
      setPendingNavigation(null);
    }, NAVIGATION_DEBOUNCE_MS);
    
    return () => clearTimeout(timeoutId);
  }, [pendingNavigation, fetchFiles]);
  
  // Initial load with debouncing
  useEffect(() => {
    if (currentPath && !pendingNavigation) {
      console.log('🎬 Initial load for path:', currentPath);
      setPendingNavigation({ path: currentPath, accessToken: accessTokens[currentPath] });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, accessTokens]);
  
  // Navigate to folder with debouncing
  const navigateToFolder = async (folderName) => {
    const newPath = currentPath === '/' ? `/${folderName}` : `${currentPath}/${folderName}`;
    
    
    console.log('🗂️ Navigating to folder:', newPath);
    
    // Clear search when navigating to a different folder
    setSearchQuery('');
    setCurrentPath(newPath);
    
    // Check if we have access token for this folder
    const accessToken = accessTokens[newPath];
    
    // Use debounced navigation to prevent rapid calls
    setPendingNavigation({ path: newPath, accessToken });
  };
  
  
  // Create password-protected folder with progress
  const handleCreateFolder = async () => {
    try {
      setCreateFolderProgress(true);

      const folderName = newFolderData.name; // Capture name before resetting

      const response = await api.post('/api/folders/create', {
        folderPath: currentPath,
        folderName: folderName,
        password: newFolderData.password,
        description: newFolderData.description
      });

      if (response.data.success) {
        showNotification('Folder created successfully!', 'success');
        setCreateFolderDialog(false);
        setNewFolderData({ name: '', password: '', description: '' });
        setShowNewFolderPassword(false);

        // Use backend cache service for intelligent invalidation
        const newFolderPath = currentPath === '/' ? `/${folderName}` : `${currentPath}/${folderName}`;
        await invalidateAfterOperation('create', newFolderPath);

        // Force refresh to show the new folder
        fetchFiles(currentPath, accessTokens[currentPath], true);
      }
    } catch (error) {
      showNotification('Failed to create folder: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setCreateFolderProgress(false);
    }
  };
  
  // Verify folder password
  const handlePasswordVerify = async () => {
    try {
      if (passwordData.action === 'delete') {
        // Handle folder deletion
        await handleFolderDeleteWithPassword(passwordData.folderPath, passwordData.folderName, passwordData.password);
        setPasswordDialog(false);
        setPasswordData({ folderPath: '', password: '' });
      } else {
        // Handle folder access
        const response = await api.post('/api/folders/verify', passwordData);
        
        if (response.data.success) {
          // Store access token for this folder
          setAccessTokens(prev => ({
            ...prev,
            [passwordData.folderPath]: response.data.accessToken
          }));
          
          setPasswordDialog(false);
          setPasswordData({ folderPath: '', password: '' });
          
          // Load folder contents with access token
          await fetchFiles(passwordData.folderPath, response.data.accessToken);
          setCurrentPath(passwordData.folderPath);
          
          showNotification('Access granted!', 'success');
        }
      }
    } catch (error) {
      const action = passwordData.action === 'delete' ? 'delete folder' : 'access folder';
      showNotification(`Failed to ${action}: ` + (error.response?.data?.error || error.message), 'error');
    }
  };
  

    // Upload files with progress - New direct upload system
  const handleFileUpload = async () => {
    const totalFiles = uploadFiles.length;
    let completedFiles = 0;

    for (const file of uploadFiles) {
      const uploadKey = `upload_${file.name}_${Date.now()}`;

      try {
        // Initialize upload progress
        setUploadProgress(prev => ({
          ...prev,
          [uploadKey]: { progress: 0, fileName: file.name, isUploading: true }
        }));

        // Step 1: Generate upload URL
        const generateUrlParams = {
          fileName: file.name,
          fileSize: file.size,
          folderPath: currentPath
        };
        if (accessTokens[currentPath]) {
          generateUrlParams.accessToken = accessTokens[currentPath];
        }
        const generateUrlResponse = await api.post('/api/files/generate-upload-url', generateUrlParams);

        if (!generateUrlResponse.data.success) {
          throw new Error(generateUrlResponse.data.error || 'Failed to generate upload URL');
        }

        const { uploadUrl, filePath } = generateUrlResponse.data; // Changed fileId to filePath
        console.log('📤 Generated upload URL for:', file.name, 'FilePath:', filePath); // Changed FileId to FilePath

        // Step 2: Upload directly to Storj using pre-signed URL
        console.log('☁️ Uploading to Storj:', uploadUrl);
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type || 'application/octet-stream'
          }
        });

        if (!uploadResponse.ok) {
          throw new Error(`Storj upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
        }

        console.log('✅ Storj upload successful for:', file.name);

        // Update progress to 90% after direct upload
        setUploadProgress(prev => ({
          ...prev,
          [uploadKey]: { ...prev[uploadKey], progress: 90 }
        }));

        // Step 3: Confirm upload completion
        console.log('🔄 Confirming upload for filePath:', filePath);
        const confirmParams = {
          filePath: filePath,
          folderPath: currentPath
        };
        if (accessTokens[currentPath]) {
          confirmParams.accessToken = accessTokens[currentPath];
        }
        const confirmResponse = await api.post('/api/files/confirm-upload', confirmParams);

        console.log('✅ Upload confirmation response:', confirmResponse.data);

        if (!confirmResponse.data.success) {
          throw new Error(confirmResponse.data.error || 'Failed to confirm upload');
        }

        // Complete upload
        setUploadProgress(prev => ({
          ...prev,
          [uploadKey]: { ...prev[uploadKey], progress: 100, isUploading: false }
        }));

        completedFiles++;
        showNotification(`Uploaded ${file.name} successfully! (${completedFiles}/${totalFiles})`, 'success');

        // Remove progress after 2 seconds
        setTimeout(() => {
          setUploadProgress(prev => {
            const updated = { ...prev };
            delete updated[uploadKey];
            return updated;
          });
        }, 2000);

      } catch (error) {
        // Clear upload progress on error
        setUploadProgress(prev => {
          const updated = { ...prev };
          delete updated[uploadKey];
          return updated;
        });

        showNotification(`Failed to upload ${file.name}: ${error.response?.data?.error || error.message}`, 'error');
      }
    }

    setUploadDialog(false);
    setUploadFiles([]);

    // Use backend cache service to handle cache invalidation for uploads
    await invalidateAfterOperation('upload', currentPath);

    // Force refresh to show uploaded files
    fetchFiles(currentPath, accessTokens[currentPath], true);
  };

  
// Download file with direct signed URL (no server streaming)
const handleFileDownload = async (filePath, fileName) => {
  const downloadKey = `${filePath}_${Date.now()}`;

  // Get access token for protected folders
  const parentFolderPath = filePath.substring(0, filePath.lastIndexOf('/')) || '/';
  const accessToken = accessTokens[parentFolderPath];

  try {
    // Initialize download progress
    setDownloadProgress(prev => ({
      ...prev,
      [downloadKey]: { progress: 0, fileName, isDownloading: true }
    }));

    const params = { filePath };
    if (accessToken) {
      params.accessToken = accessToken;
    }

    // Step 1: Get signed download URL from server
    const response = await api.get('/api/files/download', { params });

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to get download URL');
    }

    const { downloadUrl } = response.data;

    // Update progress to 50% after getting URL
    setDownloadProgress(prev => ({
      ...prev,
      [downloadKey]: { ...prev[downloadKey], progress: 50 }
    }));

    // Step 2: Download directly from Storj using signed URL
    const downloadResponse = await fetch(downloadUrl);

    if (!downloadResponse.ok) {
      throw new Error(`Download failed: ${downloadResponse.status} ${downloadResponse.statusText}`);
    }

    // Update progress to 75% after starting download
    setDownloadProgress(prev => ({
      ...prev,
      [downloadKey]: { ...prev[downloadKey], progress: 75 }
    }));

    // Convert response to blob
    const blob = await downloadResponse.blob();

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    // Complete download
    setDownloadProgress(prev => ({
      ...prev,
      [downloadKey]: { ...prev[downloadKey], progress: 100, isDownloading: false }
    }));

    // Remove progress after 2 seconds
    setTimeout(() => {
      setDownloadProgress(prev => {
        const updated = { ...prev };
        delete updated[downloadKey];
        return updated;
      });
    }, 2000);

    showNotification('File downloaded successfully!', 'success');
  } catch (error) {
    // Clear download progress on error
    setDownloadProgress(prev => {
      const updated = { ...prev };
      delete updated[downloadKey];
      return updated;
    });

    if (error.response?.data?.protected) {
      // File in password-protected folder
      setPasswordData({ folderPath: parentFolderPath, password: '' });
      setPasswordDialog(true);
    } else {
      showNotification('Failed to download file: ' + (error.response?.data?.error || error.message), 'error');
    }
  }
};





  // View file in browser
  const handleFileView = async (filePath, fileName) => {
    // Get access token for protected folders
    const parentFolderPath = filePath.substring(0, filePath.lastIndexOf('/')) || '/';

    try {
      const accessToken = accessTokens[parentFolderPath];

      const params = { filePath };
      if (accessToken) {
        params.accessToken = accessToken;
      }

      // Determine file type to decide response type
      const fileExtension = fileName.split('.').pop().toLowerCase();
      const mimeType = getMimeTypeFromExtension(fileExtension);
      const isTextFile = mimeType.startsWith('text/') ||
                        mimeType === 'application/json' ||
                        mimeType === 'application/javascript' ||
                        mimeType === 'application/typescript' ||
                        ['py', 'java', 'cpp', 'c', 'php', 'md', 'xml', 'html', 'css'].includes(fileExtension);

      const response = await api.get('/api/files/view', {
        params,
        responseType: isTextFile ? 'text' : 'blob'
      });

      let fileContent;
      if (isTextFile) {
        fileContent = response.data; // Raw text content
      } else {
        // Create object URL for binary files
        fileContent = URL.createObjectURL(new Blob([response.data], { type: mimeType }));
      }

      setViewFileData({
        filePath,
        fileName,
        fileType: mimeType,
        fileContent,
        isTextFile
      });

      setFileViewerDialog(true);
    } catch (error) {
      if (error.response?.data?.protected) {
        // File in password-protected folder
        setPasswordData({ folderPath: parentFolderPath, password: '' });
        setPasswordDialog(true);
      } else {
        showNotification('Failed to view file: ' + (error.response?.data?.error || error.message), 'error');
      }
    }
  };

  // Helper function to get MIME type from file extension
  const getMimeTypeFromExtension = (extension) => {
    const mimeTypes = {
      'txt': 'text/plain',
      'json': 'application/json',
      'xml': 'application/xml',
      'html': 'text/html',
      'css': 'text/css',
      'js': 'application/javascript',
      'ts': 'application/typescript',
      'py': 'text/x-python',
      'java': 'text/x-java-source',
      'cpp': 'text/x-c++src',
      'c': 'text/x-csrc',
      'php': 'application/x-php',
      'md': 'text/markdown',
      'pdf': 'application/pdf',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'bmp': 'image/bmp',
      'svg': 'image/svg+xml',
      'webp': 'image/webp'
    };
    return mimeTypes[extension] || 'application/octet-stream';
  };

  // Check if file can be viewed
  const canViewFile = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    const viewableExtensions = [
      'txt', 'json', 'xml', 'html', 'css', 'js', 'ts', 'py', 'java', 'cpp', 'c', 'php', 'md',
      'jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'pdf'
    ];
    return viewableExtensions.includes(extension);
  };

  // Delete file with progress indicator
  const handleFileDelete = async (filePath, fileName) => {
    if (!window.confirm(`Are you sure you want to delete "${fileName}"?`)) return;
    
    const deleteKey = `delete_${filePath}_${Date.now()}`;
    const previousFiles = [...files]; // Store backup for rollback
    
    try {
      // Initialize delete progress
      setDeleteProgress(prev => ({
        ...prev,
        [deleteKey]: { progress: 0, fileName, isDeleting: true, type: 'file' }
      }));
      
      // Simulate progress steps for better UX
      setDeleteProgress(prev => ({
        ...prev,
        [deleteKey]: { ...prev[deleteKey], progress: 25 }
      }));
      
      // Optimistic update - remove from UI immediately
      const optimisticFiles = files.filter(file => {
        const fileFullPath = currentPath === '/' ? `/${file.name}` : `${currentPath}/${file.name}`;
        return fileFullPath !== filePath;
      });
      setFiles(optimisticFiles);
      
      await api.delete('/api/files/delete', {
        data: { filePath }
      });
      
      // Update progress
      setDeleteProgress(prev => ({
        ...prev,
        [deleteKey]: { ...prev[deleteKey], progress: 75 }
      }));
      
      // Use backend cache service for intelligent cache invalidation
      console.log('🔄 Using backend cache service for file deletion:', filePath);
      await invalidateAfterOperation('delete', filePath);
      
      // Complete delete progress
      setDeleteProgress(prev => ({
        ...prev,
        [deleteKey]: { ...prev[deleteKey], progress: 100, isDeleting: false }
      }));
      
      // Remove progress after 2 seconds
      setTimeout(() => {
        setDeleteProgress(prev => {
          const updated = { ...prev };
          delete updated[deleteKey];
          return updated;
        });
      }, 2000);
      
      // Force refresh from server to ensure consistency
      setTimeout(() => {
        fetchFiles(currentPath, accessTokens[currentPath], true);
      }, 500);
      
      showNotification('File deleted successfully!', 'success');
    } catch (error) {
      // Rollback optimistic update on error
      setFiles(previousFiles);
      
      // Clear delete progress on error
      setDeleteProgress(prev => {
        const updated = { ...prev };
        delete updated[deleteKey];
        return updated;
      });
      
      // Clear cache completely and refresh from server
      console.log('💥 Error: clearing all cache and refreshing');
      cacheManager.clear();
      fetchFiles(currentPath, accessTokens[currentPath], true);
      
      showNotification('Failed to delete file: ' + (error.response?.data?.error || error.message), 'error');
    }
  };
  
  // Delete folder with progress indicator
  const handleFolderDelete = async (folderPath, folderName) => {
    if (!window.confirm(`Are you sure you want to delete folder "${folderName}" and all its contents?`)) return;
    
    // Set up password dialog for folder deletion
    setPasswordData({ folderPath, password: '', action: 'delete', folderName });
    setPasswordDialog(true);
  };
  
  // Handle folder deletion after password verification
  const handleFolderDeleteWithPassword = async (folderPath, folderName, password = '') => {
    const deleteKey = `delete_${folderPath}_${Date.now()}`;
    const previousFiles = [...files]; // Store backup for rollback
    
    try {
      // Initialize delete progress
      setDeleteProgress(prev => ({
        ...prev,
        [deleteKey]: { progress: 0, fileName: folderName, isDeleting: true, type: 'folder' }
      }));
      
      // Simulate progress steps for better UX
      setDeleteProgress(prev => ({
        ...prev,
        [deleteKey]: { ...prev[deleteKey], progress: 25 }
      }));
      
      // Optimistic update - remove from UI immediately
      const optimisticFiles = files.filter(file => {
        const fileFullPath = currentPath === '/' ? `/${file.name}` : `${currentPath}/${file.name}`;
        return fileFullPath !== folderPath;
      });
      setFiles(optimisticFiles);
      
      await api.delete('/api/folders/delete', {
        data: { folderPath: folderName, password }
      });
      
      // Update progress
      setDeleteProgress(prev => ({
        ...prev,
        [deleteKey]: { ...prev[deleteKey], progress: 75 }
      }));
      
      // Remove access token if folder was protected
      setAccessTokens(prev => {
        const updated = { ...prev };
        delete updated[folderPath];
        return updated;
      });
      
      // Use backend cache service for intelligent cache invalidation
      console.log('🔄 Using backend cache service for folder deletion:', folderPath);
      await invalidateAfterOperation('delete', folderPath);
      
      // Complete delete progress
      setDeleteProgress(prev => ({
        ...prev,
        [deleteKey]: { ...prev[deleteKey], progress: 100, isDeleting: false }
      }));
      
      // Remove progress after 2 seconds
      setTimeout(() => {
        setDeleteProgress(prev => {
          const updated = { ...prev };
          delete updated[deleteKey];
          return updated;
        });
      }, 2000);
      
      // Force refresh from server to ensure consistency
      setTimeout(() => {
        fetchFiles(currentPath, accessTokens[currentPath], true);
      }, 500);
      
      showNotification('Folder deleted successfully!', 'success');
    } catch (error) {
      // Rollback optimistic update on error
      setFiles(previousFiles);
      
      // Clear delete progress on error
      setDeleteProgress(prev => {
        const updated = { ...prev };
        delete updated[deleteKey];
        return updated;
      });
      
      // Clear cache completely and refresh from server
      console.log('💥 Folder delete error: clearing all cache and refreshing');
      cacheManager.clear();
      fetchFiles(currentPath, accessTokens[currentPath], true);
      
      showNotification('Failed to delete folder: ' + (error.response?.data?.error || error.message), 'error');
    }
  };
  
  // Show notification
  const showNotification = (message, type = 'info') => {
    setNotification({ open: true, message, type });
  };
  
  // Get file icon
  const getFileIcon = (file) => {
    if (file.isDirectory) {
      return file.protected ? (
        <Box>
          <Lock color="warning" sx={{ fontSize: '2rem' }} />
        </Box>
      ) : (
        <Box>
          <Folder color="primary" sx={{ fontSize: '2rem' }} />
        </Box>
      );
    }
    return (
      <Box>
        <InsertDriveFile color="action" sx={{ fontSize: '2rem' }} />
      </Box>
    );
  };
  
  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  // Get breadcrumb path
  const getBreadcrumbs = () => {
    const paths = currentPath.split('/').filter(Boolean);
    return [
      { name: 'Home', path: '/' },
      ...paths.map((name, index) => ({
        name,
        path: '/' + paths.slice(0, index + 1).join('/')
      }))
    ];
  };
  
  // Get current folder name for display
  const getCurrentFolderName = () => {
    if (currentPath === '/') return 'Home';
    const paths = currentPath.split('/').filter(Boolean);
    return paths[paths.length - 1];
  };
  
  // Filter files based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredFiles(files);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = files.filter(file =>
        file.name.toLowerCase().includes(query)
      );
      setFilteredFiles(filtered);
    }
  }, [files, searchQuery]);
  
  // Handle search query change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };
  
  // Clear search
  const handleClearSearch = () => {
    setSearchQuery('');
  };
  
  // Get files to display (filtered or all)
  const displayFiles = searchQuery.trim() ? filteredFiles : files;

  
  // Check if user is at root level (only allow folder creation at root)
  const isAtRoot = currentPath === '/';

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        minHeight: '100vh',
        overflowX: 'hidden',
        backgroundColor: theme.palette.background.default
      }}
    >
      {/* Header */}
      <Paper
        sx={{
          p: { xs: 1.5, sm: 2, md: 3 },
          mb: { xs: 2, sm: 3 },
          backgroundColor: theme.palette.background.paper,
          borderRadius: 1,
          boxShadow: 1,
          border: `1px solid ${theme.palette.divider}`
        }}
      >
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent={{ sm: 'space-between' }} alignItems={{ xs: 'flex-start', sm: 'center' }} gap={{ xs: 1, sm: 2 }} mb={{ xs: 1.5, sm: 2 }}>
          <Box>
          
             <Box display="flex" gap={{ xs: 0.5, sm: 1 }} mt={{ xs: 0.5, sm: 1 }}>
               {user?.location && typeof user.location === 'string' && (
                 <Chip
                   label={`📍 ${user.location.charAt(0).toUpperCase() + user.location.slice(1)}`}
                   size="small"
                   color="primary"
                   variant="outlined"
                   sx={{ fontWeight: 500, fontSize: { xs: '0.7rem', sm: '0.875rem' }, height: { xs: 24, sm: 32 } }}
                 />
               )}
               {user?.role && typeof user.role === 'string' && (
                 <Chip
                   label={user.role === 'admin' ? '👑 Admin' : '👥 User'}
                   size="small"
                   color={user.role === 'admin' ? 'error' : 'success'}
                   variant="outlined"
                   sx={{ fontWeight: 500, fontSize: { xs: '0.7rem', sm: '0.875rem' }, height: { xs: 24, sm: 32 } }}
                 />
               )}
             </Box>
          </Box>
          <Box display="flex" gap={{ xs: 0.5, sm: 1 }} alignSelf={{ xs: 'flex-end', sm: 'auto' }}>
              <Tooltip title="Refresh">
                <IconButton
                  onClick={async () => {
                    console.log('🔄 Manual refresh: using backend cache service');
                    await backendCacheService.forceRefreshFolder(currentPath);
                    fetchFiles(currentPath, accessTokens[currentPath], true);
                  }}
                  disabled={loading}
                  sx={{
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover
                    },
                    '&:disabled': {
                      opacity: 0.6
                    }
                  }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
              <Tooltip title={viewMode === 'grid' ? 'List View' : 'Grid View'}>
                <IconButton
                  onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                  sx={{
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover
                    }
                  }}
                >
                  {viewMode === 'grid' ? <ViewList /> : <GridView />}
                </IconButton>
              </Tooltip>
            </Box>
        </Box>
        
        {/* Search Box */}
        <Box mb={{ xs: 1.5, sm: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={`Search in ${getCurrentFolderName()}...`}
              value={searchQuery}
              onChange={handleSearchChange}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                      edge="end"
                      sx={{
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.error.main, 0.1),
                          color: theme.palette.error.main
                        }
                      }}
                    >
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.5)} 100%)`,
                  backdropFilter: 'blur(10px)',
                  borderRadius: 0,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.default, 0.7)} 100%)`,
                    borderColor: alpha(theme.palette.primary.main, 0.2)
                  }
                }
              }}
            />
          {searchQuery.trim() && (
            <Box mt={1}>
              <Typography variant="caption" color="text.secondary">
                {displayFiles.length === 0 
                  ? `No files found matching "${searchQuery}"`
                  : `Found ${displayFiles.length} item${displayFiles.length !== 1 ? 's' : ''} matching "${searchQuery}"`
                }
              </Typography>
            </Box>
          )}
        </Box>
        
        
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNext fontSize="small" />}
            sx={{
              '& .MuiBreadcrumbs-separator': {
                mx: 1
              }
            }}
          >
            {getBreadcrumbs().map((crumb, index) => (
              <Link
                key={index}
                  color="inherit"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    console.log('🍞 Breadcrumb navigation to:', crumb.path);
                    // Clear search when navigating to a different folder
                    setSearchQuery('');
                    setCurrentPath(crumb.path);
                    // Use debounced navigation to prevent rapid calls
                    setPendingNavigation({ path: crumb.path, accessToken: accessTokens[crumb.path] });
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    p: { xs: 0.5, sm: 1 },
                    borderRadius: 1,
                    textDecoration: 'none',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                      textDecoration: 'none'
                    }
                  }}
                >
                  {index === 0 && <Home fontSize="small" />}
                  {crumb.name}
                </Link>
            ))}
          </Breadcrumbs>
        </Paper>

      {/* Files Display */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ overflowX: 'hidden', width: '100%' }}>
          {viewMode === 'grid' ? (
            // Grid View
            <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ overflowX: 'hidden' }}>
              {displayFiles.map((file, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={file.name} sx={{ minWidth: 0 }}>
                  {file.isDirectory && file.description ? (
                    <Tooltip
                      title={file.description}
                      arrow
                      placement="top"
                      enterDelay={500}
                      enterNextDelay={50}
                      sx={{
                        '& .MuiTooltip-tooltip': {
                          backgroundColor: theme.palette.grey[800],
                          color: theme.palette.common.white,
                          fontSize: '0.875rem',
                          maxWidth: '250px',
                          wordWrap: 'break-word'
                        }
                      }}
                    >
                      <Box>
                        <Card
                          sx={{
                            cursor: 'pointer',
                            borderRadius: 1,
                            border: `1px solid ${theme.palette.divider}`,
                            backgroundColor: theme.palette.background.paper,
                            boxShadow: 1,
                            '&:hover': {
                              boxShadow: 2,
                              borderColor: theme.palette.primary.main
                            },
                            position: 'relative',
                            overflow: 'hidden',
                            minWidth: 0
                          }}
                          onClick={() => navigateToFolder(file.name)}
                        >
                          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, minWidth: 0 }}>
                            <Box display="flex" alignItems="center" mb={{ xs: 0.5, sm: 1 }} minWidth={0}>
                              {getFileIcon(file)}
                              <Typography variant="subtitle1" fontWeight={600} ml={1} noWrap sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minWidth: 0, flex: 1 }}>
                                {file.name}
                              </Typography>
                              <Box flexGrow={1} />
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setContextMenu({
                                    mouseX: e.clientX - 2,
                                    mouseY: e.clientY - 4,
                                    file
                                  });
                                }}
                                sx={{ p: { xs: 0.5, sm: 1 }, flexShrink: 0 }}
                              >
                                <MoreVert fontSize="small" />
                              </IconButton>
                            </Box>

                            <Box display="flex" flexWrap="wrap" gap={0.5} mb={{ xs: 0.5, sm: 1 }}>
                              {file.protected && (
                                <Chip label="Protected" size="small" color="warning" icon={<Lock />} />
                              )}
                              {file.owner && (
                                <Chip label={file.owner} size="small" variant="outlined" />
                              )}
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              {file.isDirectory ? 'Folder' : `${formatFileSize(file.size)}${file.uploadedBy ? ` • Uploaded by: ${file.uploadedBy}` : ''}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                              {file.created ? new Date(file.created).toLocaleString() : new Date(file.mtime).toLocaleString()}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Box>
                    </Tooltip>
                  ) : (
                    <Card
                      sx={{
                        cursor: file.isDirectory ? 'pointer' : 'default',
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                        boxShadow: 1,
                        '&:hover': file.isDirectory ? {
                          boxShadow: 2,
                          borderColor: theme.palette.primary.main
                        } : {
                          boxShadow: 2
                        },
                        position: 'relative',
                        overflow: 'hidden',
                        minWidth: 0
                      }}
                      onClick={() => {
                        if (file.isDirectory) {
                          navigateToFolder(file.name);
                        }
                      }}
                    >
                      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, minWidth: 0 }}>
                        <Box display="flex" alignItems="center" mb={{ xs: 0.5, sm: 1 }} minWidth={0}>
                          {getFileIcon(file)}
                          <Typography variant="subtitle1" fontWeight={600} ml={1} noWrap sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minWidth: 0, flex: 1 }}>
                            {file.name}
                          </Typography>
                          <Box flexGrow={1} />
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setContextMenu({
                                mouseX: e.clientX - 2,
                                mouseY: e.clientY - 4,
                                file
                              });
                            }}
                            sx={{ p: { xs: 0.5, sm: 1 }, flexShrink: 0 }}
                          >
                            <MoreVert fontSize="small" />
                          </IconButton>
                        </Box>

                        <Box display="flex" flexWrap="wrap" gap={0.5} mb={{ xs: 0.5, sm: 1 }}>
                          {file.protected && (
                            <Chip label="Protected" size="small" color="warning" icon={<Lock />} />
                          )}
                          {file.owner && (
                            <Chip label={file.owner} size="small" variant="outlined" />
                          )}
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {file.isDirectory ? 'Folder' : `${formatFileSize(file.size)}${file.uploadedBy ? ` • Uploaded by: ${file.uploadedBy}` : ''}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                          {file.created ? new Date(file.created).toLocaleString() : new Date(file.mtime).toLocaleString()}
                        </Typography>
                      </CardContent>
                    </Card>
                  )}
                </Grid>
              ))}
              </Grid>
            ) : (
              // List View
              <Paper sx={{ overflowX: 'hidden', borderRadius: 0 }}>
                <List sx={{ overflowX: 'hidden' }}>
                  {displayFiles.map((file, index) => (
                    <ListItem
                      key={file.name}
                      component={file.isDirectory ? 'div' : 'div'}
                      onClick={() => {
                        if (file.isDirectory) {
                          navigateToFolder(file.name);
                        }
                      }}
                      sx={{
                        borderRadius: 1,
                        mb: { xs: 0.5, sm: 1 },
                        cursor: file.isDirectory ? 'pointer' : 'default',
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                        '&:hover': file.isDirectory ? {
                          backgroundColor: theme.palette.action.hover,
                          boxShadow: 1,
                          borderColor: theme.palette.primary.main
                        } : {
                          backgroundColor: theme.palette.action.hover
                        },
                        position: 'relative',
                        overflow: 'hidden',
                        minWidth: 0
                      }}
                    >
                      {file.isDirectory && file.description ? (
                        <Tooltip
                          title={file.description}
                          arrow
                          placement="top"
                          enterDelay={500}
                          enterNextDelay={50}
                          sx={{
                            '& .MuiTooltip-tooltip': {
                              backgroundColor: theme.palette.grey[800],
                              color: theme.palette.common.white,
                              fontSize: '0.875rem',
                              maxWidth: '250px',
                              wordWrap: 'break-word'
                            }
                          }}
                        >
                          <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                            <ListItemIcon sx={{ minWidth: 'auto', mr: 2, flexShrink: 0 }}>
                              {getFileIcon(file)}
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                                  <Typography variant="body1" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 500, minWidth: 0, flex: 1 }} noWrap>
                                    {file.name}
                                  </Typography>
                                  {file.protected && <Lock fontSize="small" color="warning" sx={{ fontSize: { xs: '16px', sm: '20px' }, flexShrink: 0 }} />}
                                </Box>
                              }
                              secondary={
                                <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, minWidth: 0 }}>
                                  {file.isDirectory
                                    ? `Folder${file.owner ? ` • Owner: ${file.owner}` : ''}${file.created ? ` • Created: ${new Date(file.created).toLocaleString()}` : ` • Modified: ${new Date(file.mtime).toLocaleString()}`}`
                                    : `${formatFileSize(file.size)} • ${new Date(file.mtime).toLocaleString()}${file.uploadedBy ? ` • Uploaded by: ${file.uploadedBy}` : ''}`
                                  }
                                </Typography>
                              }
                              sx={{ minWidth: 0, flex: 1 }}
                            />
                            <ListItemSecondaryAction sx={{ flexShrink: 0 }}>
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setContextMenu({
                                    mouseX: e.clientX - 2,
                                    mouseY: e.clientY - 4,
                                    file
                                  });
                                }}
                              >
                                <MoreVert />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </Box>
                        </Tooltip>
                      ) : (
                        <>
                          <ListItemIcon sx={{ minWidth: 'auto', mr: 2, flexShrink: 0 }}>
                            {getFileIcon(file)}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                                <Typography variant="body1" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 500, minWidth: 0, flex: 1 }} noWrap>
                                  {file.name}
                                </Typography>
                                {file.protected && <Lock fontSize="small" color="warning" sx={{ fontSize: { xs: '16px', sm: '20px' }, flexShrink: 0 }} />}
                              </Box>
                            }
                            secondary={
                              <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, minWidth: 0 }}>
                                {file.isDirectory
                                  ? `Folder${file.owner ? ` • Owner: ${file.owner}` : ''}${file.created ? ` • Created: ${new Date(file.created).toLocaleString()}` : ` • Modified: ${new Date(file.mtime).toLocaleString()}`}`
                                  : `${formatFileSize(file.size)} • ${new Date(file.mtime).toLocaleString()}${file.uploadedBy ? ` • Uploaded by: ${file.uploadedBy}` : ''}`
                                }
                              </Typography>
                            }
                            sx={{ minWidth: 0, flex: 1 }}
                          />
                          <ListItemSecondaryAction sx={{ flexShrink: 0 }}>
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                setContextMenu({
                                  mouseX: e.clientX - 2,
                                  mouseY: e.clientY - 4,
                                  file
                                });
                              }}
                            >
                              <MoreVert />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </>
                      )}
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
        </Box>
      )}

      {/* Floating Action Button */}
      <FileOperationsSpeedDial
        onUploadClick={() => setUploadDialog(true)}
        onNewFolderClick={() => {
          setCreateFolderDialog(true);
        }}
        onRefreshClick={async () => {
          console.log('🔄 SpeedDial refresh: using backend cache service');
          await backendCacheService.forceRefreshFolder(currentPath);
          fetchFiles(currentPath, accessTokens[currentPath], true);
        }}
        isAtRoot={isAtRoot}
      />

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {contextMenu?.file?.isDirectory ? (
          [
            <MenuItem key="open" onClick={() => {
              navigateToFolder(contextMenu.file.name);
              setContextMenu(null);
            }}>
              <ListItemIcon><Visibility fontSize="small" /></ListItemIcon>
              <ListItemText>Open</ListItemText>
            </MenuItem>,
            <MenuItem key="delete" onClick={() => {
              handleFolderDelete(
                currentPath === '/' ? `/${contextMenu.file.name}` : `${currentPath}/${contextMenu.file.name}`,
                contextMenu.file.name
              );
              setContextMenu(null);
            }}>
              <ListItemIcon><Delete fontSize="small" /></ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          ]
        ) : contextMenu?.file ? (
          [
            canViewFile(contextMenu.file.name) && (
              <MenuItem key="view" onClick={() => {
                handleFileView(
                  currentPath === '/' ? `/${contextMenu.file.name}` : `${currentPath}/${contextMenu.file.name}`,
                  contextMenu.file.name
                );
                setContextMenu(null);
              }}>
                <ListItemIcon><Visibility fontSize="small" /></ListItemIcon>
                <ListItemText>View</ListItemText>
              </MenuItem>
            ),
            <MenuItem key="download" onClick={() => {
              handleFileDownload(
                currentPath === '/' ? `/${contextMenu.file.name}` : `${currentPath}/${contextMenu.file.name}`,
                contextMenu.file.name
              );
              setContextMenu(null);
            }}>
              <ListItemIcon><Download fontSize="small" /></ListItemIcon>
              <ListItemText>Download</ListItemText>
            </MenuItem>,
            <MenuItem key="delete" onClick={() => {
              handleFileDelete(
                currentPath === '/' ? `/${contextMenu.file.name}` : `${currentPath}/${contextMenu.file.name}`,
                contextMenu.file.name
              );
              setContextMenu(null);
            }}>
              <ListItemIcon><Delete fontSize="small" /></ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          ].filter(Boolean)
        ) : null}
      </Menu>

      {/* Create Folder Dialog */}
      <Dialog
        open={createFolderDialog}
        onClose={() => setCreateFolderDialog(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& .MuiDialog-paper': {
            margin: 0,
            borderRadius: 2,
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: 3,
            maxHeight: '90vh',
            overflowY: 'auto'
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            textAlign: 'center',
            fontWeight: 600
          }}
        >
          📁 Create New Folder
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="body2" color="info.dark">
              📁 Creating folder in: {currentPath === '/' ? 'Home' : currentPath}
            </Typography>
          </Box>
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            variant="outlined"
            value={newFolderData.name}
            onChange={(e) => setNewFolderData(prev => ({ ...prev, name: e.target.value }))}
            sx={{ mb: 2 }}
            required
          />
          {isAtRoot && (
            <TextField
              margin="dense"
              label="Password (required)"
              type={showNewFolderPassword ? 'text' : 'password'}
              fullWidth
              variant="outlined"
              value={newFolderData.password}
              onChange={(e) => setNewFolderData(prev => ({ ...prev, password: e.target.value }))}
              sx={{ mb: 2 }}
              required
              helperText="Password is required for root level folders"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewFolderPassword(!showNewFolderPassword)}
                      edge="end"
                      size="small"
                    >
                      {showNewFolderPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          )}
          <TextField
            margin="dense"
            label="Description (optional)"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={newFolderData.description}
            onChange={(e) => setNewFolderData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Add a description for this folder..."
            helperText="Optional: Provide additional information about this folder"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFolderDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreateFolder}
            variant="contained"
            disabled={!newFolderData.name || createFolderProgress}
            startIcon={createFolderProgress ? <CircularProgress size={20} /> : <CreateNewFolder />}
          >
            {createFolderProgress ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Dialog */}
      <Dialog
        open={passwordDialog}
        onClose={() => setPasswordDialog(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& .MuiDialog-paper': {
            margin: 0,
            borderRadius: 2,
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: 3,
            maxHeight: '90vh',
            overflowY: 'auto'
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: passwordData.action === 'delete' ? theme.palette.error.main : theme.palette.primary.main,
            color: 'white',
            textAlign: 'center',
            fontWeight: 600
          }}
        >
          {passwordData.action === 'delete' ? '🗑️ Folder Deletion' : '🔐 Folder Password Required'}
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {passwordData.action === 'delete' 
              ? `Enter the password to delete folder "${passwordData.folderName || 'this folder'}" (leave empty if not protected).`
              : 'This folder is password protected. Please enter the password to access it.'
            }
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Password"
            type={showVerifyPassword ? 'text' : 'password'}
            fullWidth
            variant="outlined"
            value={passwordData.password}
            onChange={(e) => setPasswordData(prev => ({ ...prev, password: e.target.value }))}
            helperText={passwordData.action === 'delete' ? 'Leave empty if folder is not password protected' : ''}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowVerifyPassword(!showVerifyPassword)}
                    edge="end"
                    size="small"
                  >
                    {showVerifyPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialog(false)}>Cancel</Button>
          <Button 
            onClick={handlePasswordVerify} 
            variant="contained" 
            disabled={passwordData.action !== 'delete' && !passwordData.password}
            color={passwordData.action === 'delete' ? 'error' : 'primary'}
          >
            {passwordData.action === 'delete' ? 'Delete Folder' : 'Access Folder'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialog}
        onClose={() => setUploadDialog(false)}
        maxWidth="md"
        fullWidth
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& .MuiDialog-paper': {
            margin: 0,
            borderRadius: 2,
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: 3,
            maxHeight: '90vh',
            overflowY: 'auto'
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: theme.palette.info.main,
            color: 'white',
            textAlign: 'center',
            fontWeight: 600
          }}
        >
          📤 Upload Files
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              border: `2px dashed ${theme.palette.primary.main}`,
              borderRadius: 1,
              p: 4,
              textAlign: 'center',
              backgroundColor: theme.palette.action.hover,
              cursor: 'pointer',
              '&:hover': {
                borderColor: theme.palette.primary.dark,
                backgroundColor: theme.palette.action.selected
              }
            }}
            onClick={() => document.getElementById('file-input').click()}
          >
            <CloudUpload fontSize="large" color="primary" />
            <Typography variant="h6" gutterBottom>
              Click to select files or drag and drop
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload to: {currentPath}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Select individual files to upload
            </Typography>
          </Box>
          
          <input
            id="file-input"
            type="file"
            multiple
            hidden
            onChange={(e) => setUploadFiles(Array.from(e.target.files))}
          />
          
          {uploadFiles.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle1" gutterBottom>
                Selected Files ({uploadFiles.length}):
              </Typography>
              <Box sx={{ maxHeight: 200, overflowY: 'auto', border: `1px solid ${alpha(theme.palette.divider, 0.2)}`, borderRadius: 1, p: 1 }}>
                <List dense>
                  {uploadFiles.map((file, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
                        <InsertDriveFile fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" noWrap>
                            {file.webkitRelativePath || file.name}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {formatFileSize(file.size)}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialog(false)}>Cancel</Button>
          <Button
            onClick={handleFileUpload}
            variant="contained"
            disabled={uploadFiles.length === 0}
            startIcon={<CloudUpload />}
            sx={{ minWidth: 150 }}
          >
            Upload {uploadFiles.length} File{uploadFiles.length !== 1 ? 's' : ''}
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Viewer Dialog */}
      <Dialog
        open={fileViewerDialog}
        onClose={() => {
          setFileViewerDialog(false);
          // Clean up object URL to prevent memory leaks (only for binary files)
          if (viewFileData.fileContent && !viewFileData.isTextFile) {
            URL.revokeObjectURL(viewFileData.fileContent);
          }
          setViewFileData({ filePath: '', fileName: '', fileType: '', fileContent: null, isTextFile: false });
        }}
        maxWidth="lg"
        fullWidth
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& .MuiDialog-paper': {
            margin: 0,
            borderRadius: 2,
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: 3,
            maxHeight: '90vh',
            overflowY: 'auto',
            width: '90vw',
            maxWidth: '1200px'
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            textAlign: 'center',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Description />
          Viewing: {viewFileData.fileName}
        </DialogTitle>
        <DialogContent sx={{ p: 0, minHeight: '400px' }}>
          {viewFileData.fileContent && (
            <Box sx={{ width: '100%', height: '100%', minHeight: '400px' }}>
              {viewFileData.fileType.startsWith('image/') ? (
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <img
                    src={viewFileData.fileContent}
                    alt={viewFileData.fileName}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '70vh',
                      objectFit: 'contain',
                      borderRadius: '4px'
                    }}
                  />
                </Box>
              ) : viewFileData.fileType === 'application/pdf' ? (
                <Box sx={{ width: '100%', height: '70vh' }}>
                  <iframe
                    src={viewFileData.fileContent}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      borderRadius: '4px'
                    }}
                    title={viewFileData.fileName}
                  />
                </Box>
              ) : viewFileData.isTextFile ? (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" component="pre" sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    backgroundColor: theme.palette.grey[50],
                    p: 2,
                    borderRadius: 1,
                    maxHeight: '60vh',
                    overflow: 'auto',
                    border: `1px solid ${theme.palette.divider}`
                  }}>
                    {viewFileData.fileContent}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Description sx={{ fontSize: '4rem', color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    File Preview Not Available
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This file type cannot be previewed in the browser. Please download the file to view its contents.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setFileViewerDialog(false);
            if (viewFileData.fileContent && !viewFileData.isTextFile) {
              URL.revokeObjectURL(viewFileData.fileContent);
            }
            setViewFileData({ filePath: '', fileName: '', fileType: '', fileContent: null, isTextFile: false });
          }}>
            Close
          </Button>
          <Button
            onClick={() => handleFileDownload(viewFileData.filePath, viewFileData.fileName)}
            variant="contained"
            startIcon={<Download />}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      {/* Download Progress Indicators */}
      <Box
        sx={{
          position: 'fixed',
          top: 20,
          right: 20,
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          maxWidth: 300,
          overflowX: 'hidden',
          maxHeight: '80vh',
          overflowY: 'auto'
        }}
      >
        {Object.entries(downloadProgress).map(([key, progress]) => (
          <Box key={key}>
            <Paper
              elevation={3}
              sx={{
                p: 2.5,
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                boxShadow: 2
              }}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Box position="relative" display="inline-flex">
                  <CircularProgress
                    variant="determinate"
                    value={progress.progress}
                    size={40}
                    sx={{
                      color: progress.progress === 100 ? theme.palette.success.main : theme.palette.primary.main
                    }}
                  />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      component="div"
                      color="text.secondary"
                      fontSize="10px"
                      fontWeight={600}
                    >
                      {progress.progress === 100 ? '✓' : `${progress.progress}%`}
                    </Typography>
                  </Box>
                </Box>
                
                <Box flex={1} minWidth={0} sx={{ overflowX: 'hidden' }}>
                  <Typography variant="body2" fontWeight={600} noWrap sx={{ minWidth: 0 }}>
                    {progress.isDownloading ? 'Downloading...' : 'Downloaded'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                    {progress.fileName}
                  </Typography>
                </Box>
                
                <IconButton
                  size="small"
                  onClick={() => {
                    setDownloadProgress(prev => {
                      const updated = { ...prev };
                      delete updated[key];
                      return updated;
                    });
                  }}
                  sx={{ p: 0.5 }}
                >
                  <Download
                    fontSize="small"
                    sx={{
                      color: progress.progress === 100 ? theme.palette.success.main : theme.palette.primary.main
                    }}
                  />
                </IconButton>
              </Box>
            </Paper>
          </Box>
        ))}
        
        {/* Upload Progress Indicators */}
        {Object.entries(uploadProgress).map(([key, progress]) => (
          <Box key={key}>
            <Paper
              elevation={3}
              sx={{
                p: 2.5,
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                boxShadow: 2
              }}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Box position="relative" display="inline-flex">
                  <CircularProgress
                    variant="determinate"
                    value={progress.progress}
                    size={40}
                    sx={{
                      color: progress.progress === 100 ? theme.palette.success.main : theme.palette.info.main
                    }}
                  />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      component="div"
                      color="text.secondary"
                      fontSize="10px"
                      fontWeight={600}
                    >
                      {progress.progress === 100 ? '✓' : `${progress.progress}%`}
                    </Typography>
                  </Box>
                </Box>
                
                <Box flex={1} minWidth={0} sx={{ overflowX: 'hidden' }}>
                  <Typography variant="body2" fontWeight={600} noWrap sx={{ minWidth: 0 }}>
                    {progress.isUploading ? 'Uploading...' : 'Uploaded'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                    {progress.fileName}
                  </Typography>
                </Box>
                
                <IconButton
                  size="small"
                  onClick={() => {
                    setUploadProgress(prev => {
                      const updated = { ...prev };
                      delete updated[key];
                      return updated;
                    });
                  }}
                  sx={{ p: 0.5 }}
                >
                  <CloudUpload
                    fontSize="small"
                    sx={{
                      color: progress.progress === 100 ? theme.palette.success.main : theme.palette.info.main
                    }}
                  />
                </IconButton>
              </Box>
            </Paper>
          </Box>
        ))}
        
        {/* Delete Progress Indicators */}
        {Object.entries(deleteProgress).map(([key, progress]) => (
          <Box key={key}>
            <Paper
              elevation={3}
              sx={{
                p: 2.5,
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                boxShadow: 2
              }}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Box position="relative" display="inline-flex">
                  <CircularProgress
                    variant="determinate"
                    value={progress.progress}
                    size={40}
                    sx={{
                      color: progress.progress === 100 ? theme.palette.success.main : theme.palette.error.main
                    }}
                  />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      component="div"
                      color="text.secondary"
                      fontSize="10px"
                      fontWeight={600}
                    >
                      {progress.progress === 100 ? '✓' : `${progress.progress}%`}
                    </Typography>
                  </Box>
                </Box>
                
                <Box flex={1} minWidth={0} sx={{ overflowX: 'hidden' }}>
                  <Typography variant="body2" fontWeight={600} noWrap sx={{ minWidth: 0 }}>
                    {progress.isDeleting ? `Deleting ${progress.type}...` : `${progress.type === 'folder' ? 'Folder' : 'File'} deleted`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                    {progress.fileName}
                  </Typography>
                </Box>
                
                <IconButton
                  size="small"
                  onClick={() => {
                    setDeleteProgress(prev => {
                      const updated = { ...prev };
                      delete updated[key];
                      return updated;
                    });
                  }}
                  sx={{ p: 0.5 }}
                >
                  <Delete
                    fontSize="small"
                    sx={{
                      color: progress.progress === 100 ? theme.palette.success.main : theme.palette.error.main
                    }}
                  />
                </IconButton>
              </Box>
            </Paper>
          </Box>
        ))}
      </Box>

    



     
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          severity={notification.type}
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EnhancedFileManager;













